import { CATALOG, type Part } from './catalog';
import { NFPA_LETTER, standardOf } from './standards';
import { newId } from './tagging';
import type { Page, Project, Pt, PType, Sym, SymProps } from './types';
import { t as $t, tk } from './i18n';


/*
 * Parametric circuit generation (EPLAN "Engineering Configuration" in small):
 * a motor list becomes supply, power, control and PLC pages with devices,
 * terminals, cables and parts chosen from the parts list by rating.
 *
 * Sheet grid: one current path per 40 mm column, x = 32 + 40·k (k = 1…9).
 */

export type StarterKind = 'dol' | 'rev' | 'yd' | 'vfd' | 'ss';
/**
 * local: buttons and lamps; plc: interposing relays and feedbacks; auto: a hand-off-auto selector, buttons in hand,
 * a field contact (float, pressure switch…) in auto; autoplc: the same with the PLC in auto
 */
export type ControlKind = 'local' | 'plc' | 'auto' | 'autoplc';

export interface MotorSpec {
  /** motor tag; empty = next free M number */
  tag?: string;
  desc: string;
  /** rated power in kW */
  kw: number;
  kind: StarterKind;
  control: ControlKind;
  /** cable length in metres */
  cableLen?: number;
  /** frequency converter under PLC (or hand-auto) control: speed set point from a PLC analog output */
  speedRef?: boolean;
  /** PTC thermistors in the motor windings: a thermistor relay in the contactor's control rung */
  ptc?: boolean;
}

/** A field signal on the PLC that is not part of a motor starter. */
export type IoKind = 'switch_no' | 'switch_nc' | 'lamp' | 'valve' | 'analog' | 'valve_prop';
export interface IoSpec {
  /** device tag; empty = next free B (switches), H (lamps) or MB (valves) */
  tag?: string;
  desc: string;
  /** switch_no / switch_nc: a field contact (level, pressure, flow, limit switch) on a digital input;
   *  lamp: a 24 V DC signal lamp and valve: a 24 V DC solenoid valve on a digital output;
   *  analog: a 2-wire 4-20 mA transmitter (pressure, level, temperature, flow) on an analog input;
   *  valve_prop: a 4-20 mA proportional valve on an analog output */
  kind: IoKind;
  /** switches: the device, when the type column names it (otherwise read from the description) */
  sensor?: SensorKind;
}
export const IO_KINDS: IoKind[] = ['switch_no', 'switch_nc', 'lamp', 'valve', 'analog', 'valve_prop'];

export interface GenOptions {
  /** field signals besides the motors, drawn on the PLC pages */
  io?: IoSpec[];
  /** emergency stop buttons (0–6): a template e-stop circuit with a safety relay that switches off the control voltage */
  estop?: number;
  /** parts to choose from (default: built-in catalogue) */
  parts?: Part[];
  /** add the supply page (busbars, control fuse, 24 V supply); default: when the project has none */
  supply?: boolean;
}

export interface GenResult {
  pages: Page[];
  warnings: string[];
  /** power boards: compartment groups from left to right (see SectionLayoutOptions.groups) */
  groups?: { name: string; bus: number; pages: string[] }[];
}

export const STARTER_NAMES: Record<StarterKind, string> = {
  dol: tk("Direkt yol verme"),
  rev: tk("İleri-geri"),
  yd: tk("Yıldız-üçgen"),
  vfd: tk("Frekans konvertörü"),
  ss: tk("Soft starter"),
};
export const CONTROL_NAMES: Record<ControlKind, string> = { local: tk("Yerel buton"), plc: 'PLC', auto: tk("El/Otomatik"), autoplc: tk("El/Otomatik (PLC)") };
/** The PLC commands or watches the motor. */
const usesPlc = (m: MotorSpec) => m.control === 'plc' || m.control === 'autoplc';
/** A hand-off-auto selector in the control circuit. */
const handAutoOf = (m: MotorSpec) => m.control === 'auto' || m.control === 'autoplc';

const X = (k: number) => 32 + 40 * k;
const COLS = 9; // usable columns k = 1…9
const f1 = (v: number) => String(Math.round(v * 100) / 100).replace('.', ',');

/** Rated current at 400 V from power, with typical cos φ · η per size. */
export function ratedCurrent(kw: number): number {
  // typical cos φ · η of 4-pole IE2/IE3 motors (e.g. 1,5 kW ≈ 3,5 A, 15 kW ≈ 29 A)
  const k = kw <= 0.75 ? 0.57 : kw <= 1.5 ? 0.62 : kw <= 4 ? 0.7 : kw <= 11 ? 0.74 : kw <= 30 ? 0.77 : 0.8;
  return (kw * 1000) / (Math.sqrt(3) * 400 * k);
}

/** Copper cross-section (mm²) for a current, installation method C, 3 loaded cores. */
export function cableSection(amps: number): string {
  const table: [number, string][] = [[15, '1.5'], [21, '2.5'], [28, '4'], [36, '6'], [50, '10'], [68, '16'], [89, '25'], [110, '35'], [134, '50']];
  return (table.find(([a]) => amps <= a) ?? [0, '70'])[1];
}

/* ---------------- part selection by rating ---------------- */

const num = (s: string) => parseFloat(s.replace(',', '.'));
export const rangeOf = (d: string): [number, number] | null => {
  const m = /(\d+(?:[.,]\d+)?)\s*[–-]\s*(\d+(?:[.,]\d+)?)\s*A\b/.exec(d);
  return m ? [num(m[1]), num(m[2])] : null;
};
/** Rated current from a description: "C16", "160 A", "400/5 A" (not "30 mA"). */
export const ratedOf = (d: string): number | null => {
  // current transformers: the primary of "2000/5 A" (not the 5 A secondary written elsewhere in the text)
  const ct = /\b(\d{2,5})\s*\/\s*[15]\s*A?(?![\d.,])/.exec(d);
  if (ct) return +ct[1];
  const c = /\b[BCDK](\d+)\b/.exec(d);
  if (c) return +c[1];
  const m = /(\d+(?:[.,]\d+)?)\s*(?:\/\s*\d+\s*)?A\b/.exec(d);
  return m ? num(m[1]) : null;
};
const powerOf = (d: string): number | null => {
  const m = /(\d+(?:[.,]\d+)?)\s*kW/.exec(d);
  return m ? num(m[1]) : null;
};

export interface PartWant {
  /** current that must lie inside the part's "a–b A" setting range */
  amps?: number;
  /** minimum power in kW (smallest suitable is chosen) */
  minKw?: number;
  /** exact power in kW */
  kw?: number;
  /** minimum rated current in A (smallest suitable is chosen): breakers "C16", "160 A", current transformers "400/5 A" */
  rated?: number;
  /** text the description must contain, e.g. "230 V AC" */
  text?: string;
  no?: number;
  nc?: number;
  /** main poles (4 = switched neutral); parts that do not say are accepted */
  poles?: number;
}

/** Chooses the best part of a symbol type for a rating, from descriptions like "2,8–4 A" or "7,5 kW". */
export function pickPart(parts: Part[], type: string, w: PartWant): Part | undefined {
  let best: Part | undefined, score = Infinity;
  for (const p of parts) {
    if (!p.t?.includes(type)) continue;
    if (w.text && !p.d.includes(w.text)) continue;
    if ((w.no ?? 0) > (p.no ?? 0) || (w.nc ?? 0) > (p.nc ?? 0)) continue;
    if (w.poles != null && (p.poles ?? (/4\s*(p|kutup)/i.test(p.d) ? 4 : 3)) !== w.poles) continue;
    let s: number;
    if (w.amps != null) {
      const r = rangeOf(p.d);
      if (!r || w.amps < r[0] || w.amps > r[1]) continue;
      s = r[1];
    } else if (w.rated != null) {
      const r = ratedOf(p.d);
      if (r == null || r < w.rated) continue;
      s = r;
    } else if (w.kw != null) {
      const k = powerOf(p.d);
      if (k == null || Math.abs(k - w.kw) > 0.01) continue;
      s = 0;
    } else if (w.minKw != null) {
      const k = powerOf(p.d);
      if (k == null || k < w.minKw - 1e-9) continue;
      s = k;
    } else s = 0;
    if (s < score) {
      best = p;
      score = s;
    }
  }
  return best;
}

/* ---------------- motor list import ---------------- */

// toLowerCase, not toLocaleLowerCase(): the default locale may turn "I" into the Turkish dotless "ı"
const squash = (v: string) => v.normalize('NFC').toLowerCase().replace(/[\s\-–]+/g, '');
/** The cell names this (translated) item, in full or by a part of it ("Direct" for "Direct-on-line starting"). */
const matchesName = (cell: string, name: string) => {
  const c = squash(cell), n = squash(name);
  return !!n && !!c && (c.includes(n) || (c.length >= 3 && n.includes(c)));
};
export function parseStarter(v: string): StarterKind | null {
  const t = v.toLocaleLowerCase('tr').replace(/\s+/g, '');
  // the starter names of the interface language ("Star-delta", "Stern-Dreieck", "星三角" …)
  const own = (Object.keys(STARTER_NAMES) as StarterKind[]).find((k) => matchesName(v, $t(STARTER_NAMES[k])));
  if (own) return own;
  if (/soft|yumu[sş]ak|sanft|^ss$/.test(t)) return 'ss';
  if (!t || /^(dol|direkt|dyv|direct)/.test(t)) return 'dol';
  if (/ileri|geri|rev|yön/.test(t)) return 'rev';
  if (/yıldız|yildiz|y\/d|y\/δ|yd|star|üçgen/.test(t)) return 'yd';
  if (/frekans|vfd|inv|sürücü|surucu|fk|drive|hız/.test(t)) return 'vfd';
  return null;
}
export function parseControl(v: string): ControlKind {
  if (matchesName(v, $t(CONTROL_NAMES.local))) return 'local';
  // hand and auto in one cell: the whole name ("Otomatik" alone is PLC control), "El/Oto", "oto manuel", "Hand-Auto",
  // "HOA"; with the PLC in auto when the cell says so
  const t = v.toLocaleLowerCase('tr');
  if (squash(v).includes(squash($t(CONTROL_NAMES.auto))) || (/(^|[^a-zçğıöşü])(el|man[a-zçğıöşü]*|hand)([^a-zçğıöşü]|$)/.test(t) && /oto|auto/.test(t)) || /\bhoa\b/.test(t))
    return /plc|scada/.test(t) ? 'autoplc' : 'auto';
  return /plc|otomatik|auto|scada/i.test(v) ? 'plc' : 'local';
}

/**
 * Motor list pasted from Excel (tab separated) or CSV: tag, description, kW,
 * starter, control, cable length. A header row is skipped.
 */
export function parseMotorList(text: string): { motors: MotorSpec[]; errors: string[] } {
  const motors: MotorSpec[] = [], errors: string[] = [];
  // trim spaces but keep tabs: an empty first column (no tag) must stay a column
  const lines = text.split(/\r?\n/).map((l) => l.replace(/^ +| +$/g, '')).filter((l) => l.trim());
  lines.forEach((line, i) => {
    const c = line.split(line.includes('\t') ? '\t' : /[;,](?=(?:[^"]*"[^"]*")*[^"]*$)/).map((x) => x.trim().replace(/^"|"$/g, ''));
    const kw = num(c[2] ?? '');
    if (!Number.isFinite(kw)) {
      if (i === 0) return; // header
      return void errors.push($t("{0}. satır: güç (kW) okunamadı", i + 1));
    }
    if (kw <= 0 || kw > 500) return void errors.push($t("{0}. satır: güç 0–500 kW arasında olmalı", i + 1));
    const kind = parseStarter(c[3] ?? '');
    if (!kind) return void errors.push($t("{0}. satır: yol verme tipi anlaşılamadı (\"{1}\")", i + 1, c[3]));
    const len = num(c[5] ?? '');
    motors.push({
      tag: (c[0] ?? '').replace(/^[-=+\s]+/, '').replace(/\s+/g, '').toUpperCase(),
      desc: c[1] ?? '',
      kw,
      kind,
      control: parseControl(c[4] ?? ''),
      ...(Number.isFinite(len) && len > 0 ? { cableLen: len } : {}),
    });
    // optional 7th column: the PLC sets the speed of a drive through an analog output
    const m = motors[motors.length - 1];
    if (m.kind === 'vfd' && usesPlc(m) && /^(evet|e|yes|y|ja|oui|sí|si|sim|да|是|はい|نعم|हाँ|x|1|var|analog|ao|true|✓)$/i.test(c[6] ?? '')) m.speedRef = true;
    // optional 8th column: PTC thermistors in the windings
    if (/^(evet|e|yes|y|ja|oui|sí|si|sim|да|是|はい|نعم|हाँ|x|1|var|true|✓|ptc|termistör|termistor|thermistor)$/i.test(c[7] ?? '')) m.ptc = true;
  });
  return { motors, errors };
}

export const IO_NAMES: Record<IoKind, string> = {
  switch_no: tk('Şalter NO'),
  switch_nc: tk('Şalter NC'),
  lamp: tk('Lamba'),
  valve: tk('Selenoid vana'),
  analog: tk('Transmitter 4-20 mA'),
  valve_prop: tk('Oransal vana'),
};

/** Field switch device, drawn with its own actuator (IEC 60617) or IEEE 315 form. */
export type SensorKind = 'limit' | 'float' | 'pressure' | 'flow' | 'temp' | 'inductive' | 'proximity';
const SENSOR_WORDS: [SensorKind, RegExp][] = [
  ['inductive', /endüktif|enduktif|indükt|induct|induktiv/],
  ['proximity', /yaklaşım|yaklasim|proximity|kapasitif|capacitive|kapazitiv|näherung/],
  ['float', /şamandıra|samandira|flatör|flator|float|schwimmer/],
  ['flow', /akış|akis|debi|flow|strömung|durchfluss/],
  ['pressure', /presostat|basın[çc]|basinc|pressure|pressostat|druck/],
  ['temp', /termostat|thermostat|sıcaklı|sicakli|temperatur/],
  ['limit', /limit|sınır|sinir|konum|kapı|kapi|door|position|endschalter/],
];
/** The switch device a text names (type cell or description), if any. */
export function parseSensor(v: string): SensorKind | undefined {
  const t = v.toLocaleLowerCase('tr');
  return SENSOR_WORDS.find(([, re]) => re.test(t))?.[0];
}
/** Library symbol of a field switch; a plain position switch when the device is unknown. */
export function sensorSymbol(sensor: SensorKind | undefined, nc: boolean): string {
  const k = nc ? 'nc' : 'no';
  switch (sensor) {
    case 'float': return `fs_${k}`;
    case 'pressure': return `ps_${k}`;
    case 'flow': return `flow_${k}`;
    case 'temp': return nc ? 'tstat_nc' : 'tstat';
    case 'inductive': return `ind_${k}`;
    case 'proximity': return `prox_${k}`;
    default: return `ls_${k}`;
  }
}

/** Signal type of a cell: the names of the interface language first, then Turkish and English keywords. */
export function parseIoKind(v: string): IoKind | null {
  const own = (Object.keys(IO_NAMES) as IoKind[]).find((k) => matchesName(v, $t(IO_NAMES[k])) || squash(v) === squash(k));
  if (own) return own;
  const t = v.toLocaleLowerCase('tr');
  if (/oransal|prop|kontrol vana|control valve/.test(t)) return 'valve_prop';
  if (/analog|4.?-?20|transmit|trafo/.test(t)) return 'analog';
  if (/lamba|lamp|ışık|isik|light/.test(t)) return 'lamp';
  if (/vana|valf|valve|selenoid|solenoid/.test(t)) return 'valve';
  if (/\bnc\b|açan|acan|kapalı|closed|öffner/.test(t)) return 'switch_nc';
  if (/\bno\b|kapanan|açık|open|şalter|salter|switch|şamandıra|samandira|flatör|presostat|termostat|sensör|sensor/.test(t)) return 'switch_no';
  return null;
}

/** Field signal list pasted from Excel or CSV: tag, description, type. A header row is skipped. */
export function parseIoList(text: string): { io: IoSpec[]; errors: string[] } {
  const io: IoSpec[] = [], errors: string[] = [];
  const lines = text.split(/\r?\n/).map((l) => l.replace(/^ +| +$/g, '')).filter((l) => l.trim());
  lines.forEach((line, i) => {
    const c = line.split(line.includes('\t') ? '\t' : /[;,](?=(?:[^"]*"[^"]*")*[^"]*$)/).map((x) => x.trim().replace(/^"|"$/g, ''));
    const kind = parseIoKind(c[2] ?? '');
    if (!kind) {
      if (i === 0) return; // header
      return void errors.push($t('{0}. satır: sinyal türü anlaşılamadı ("{1}")', i + 1, c[2] ?? ''));
    }
    const sensor = kind === 'switch_no' || kind === 'switch_nc' ? parseSensor(c[2] ?? '') : undefined;
    io.push({ tag: (c[0] ?? '').replace(/^[-=+\s]+/, '').replace(/\s+/g, ''), desc: c[1] ?? '', kind, ...(sensor ? { sensor } : {}) });
  });
  return { io, errors };
}

/* ---------------- generator ---------------- */

interface Signal {
  /** ai: a transmitter on an analog input; ao: an analog output driving a load (drive set point, proportional valve) */
  dir: 'in' | 'out' | 'ai' | 'ao';
  desc: string;
  /** input: contact that switches the input (a motor's own contact, or a field switch drawn in full) */
  contact?: { tag: string; type: string; desc?: string };
  /** output: the interposing relay it drives */
  relay?: string;
  /** output: a 24 V load driven directly (signal lamp, solenoid valve) */
  load?: { tag: string; type: 'lamp_led' | 'sol' | 'vfd_ai' | 'vprop'; desc: string };
}

export function generateMotorCircuits(pr: Project, specs: MotorSpec[], opt: GenOptions = {}): GenResult {
  const parts = opt.parts ?? CATALOG;
  const warnings: string[] = [];
  const pages: Page[] = [];
  const has = (pn: string) => (parts.some((p) => p.pn === pn) ? pn : '');

  // next free number per tag prefix, across the project and everything generated here
  const used = new Map<string, number>();
  for (const pg of pr.pages)
    for (const s of pg.syms) {
      const m = /^([A-Z]+)(\d+)$/.exec(s.tag || '');
      if (m) used.set(m[1], Math.max(used.get(m[1]) ?? 0, +m[2]));
    }
  // NFPA 79 projects get NFPA designations (CB, M, CR, OL, PB, LT…)
  const nfpa = standardOf(pr) === 'nfpa';
  // starters are "M" in NFPA 79, unless the motor list itself already names motors M1, M2…
  const starter = specs.some((m) => /^M\d+$/.test(m.tag || '')) ? 'MS' : 'M';
  const tag = (iec: string, nfpaPrefix?: string) => {
    const prefix = nfpa ? nfpaPrefix ?? NFPA_LETTER[iec] ?? iec : iec;
    const n = (used.get(prefix) ?? 0) + 1;
    used.set(prefix, n);
    return prefix + n;
  };
  const claim = (t: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec(t);
    if (m) used.set(m[1], Math.max(used.get(m[1]) ?? 0, +m[2]));
  };
  let term = 0;
  for (const pg of pr.pages) for (const s of pg.syms) if (s.type === 'term' && s.tag === 'X1') term = Math.max(term, parseInt(s.props.pinNo || '0', 10) || 0);
  const taken = new Set(pr.pages.flatMap((p) => p.syms.map((s) => s.tag)));

  const page = (desc: string) => {
    const p: Page = { id: newId(), desc, syms: [], wires: [] };
    pages.push(p);
    return p;
  };
  const S = (p: Page, type: string, x: number, y: number, o: { rot?: number; tag?: string; desc?: string; part?: string; props?: SymProps } = {}) => {
    const s: Sym = { id: newId(), type, x, y, rot: o.rot || 0, tag: o.tag || '', desc: o.desc || '', part: o.part || '', maker: '', props: o.props || {} };
    if (s.part) s.maker = parts.find((q) => q.pn === s.part)?.maker ?? '';
    p.syms.push(s);
    return s;
  };
  const W = (p: Page, pts: Pt[], cs = '', col = '') => p.wires.push({ id: newId(), pts, cs, col });
  const choose = (who: string, what: string, type: string, w: PartWant) => {
    const p = pickPart(parts, type, w);
    if (!p) warnings.push($t("{0}: {1} için uygun parça bulunamadı{2}", who, what, w.amps != null ? ` (${f1(w.amps)} A)` : w.minKw != null ? ` (≥ ${f1(w.minKw)} kW)` : ''));
    return p?.pn ?? '';
  };

  const ioSpecs = opt.io ?? [];
  const needsPlc = specs.some(usesPlc) || ioSpecs.length > 0;
  /** the motor has a 230 V control rung: contactors, or the start relay of a soft starter or drive (not under the PLC) */
  // a drive under local control is started by push buttons in the panel, as a soft starter is (not from its keypad)
  const rung = (m: MotorSpec) => (m.kind === 'vfd' || m.kind === 'ss' ? m.control !== 'plc' : true);
  const nStop = Math.max(0, Math.min(6, Math.round(opt.estop ?? 0)));
  // the safety relay runs on 24 V DC, so an e-stop circuit needs the 24 V supply even without a PLC
  const needs24 = needsPlc || nStop > 0;
  const safety = nStop ? tag('K') : '';
  let safetyWired = false;
  // tags the signal list gives are reserved before anything is numbered, so generated lamps and switches skip them
  const ioTags = ioSpecs.map((io) => {
    const t = io.tag || '';
    if (!t) return '';
    if (taken.has(t)) {
      warnings.push($t("{0} etiketi projede zaten var; yeni etiket verildi", t));
      return '';
    }
    claim(t);
    taken.add(t);
    return t;
  });
  const hasBrk = (name: string) => pr.pages.some((p) => p.syms.some((s) => s.type === 'brk' && s.props.name === name));
  const hasPot = pr.pages.some((p) => p.syms.some((s) => s.type === 'pot' && s.props.name === 'L1'));
  const supply = opt.supply ?? !hasBrk('L1');
  if (hasPot && !hasBrk('L1'))
    warnings.push($t("Projede L1…PE baraları kesinti noktası olmadan çizilmiş; yeni sayfaların baraları onlara bağlanmaz. Mevcut baraların ucuna L1, L2, L3, N, PE kesinti noktaları ekleyin."));
  const BUS: [string, number, PType, string][] = [['L1', 24, 'L', 'BK'], ['L2', 32, 'L', 'BK'], ['L3', 40, 'L', 'BK'], ['N', 48, 'N', 'BU'], ['PE', 56, 'PE', 'GNYE']];
  /** busbars across the page; `onward`: they continue on a following page */
  const busbars = (p: Page, first: boolean, onward = true) =>
    BUS.forEach(([n, y, t, col]) => {
      if (first) S(p, 'pot', 28, y, { props: { name: n, ptype: t } });
      else S(p, 'brk', 28, y, { rot: 1, props: { name: n, ptype: t } });
      W(p, [[28, y], [400, y]], '', col);
      if (onward) S(p, 'brk', 400, y, { rot: 3, props: { name: n, ptype: t } });
    });

  /* ---- supply page ---- */
  if (supply || (needs24 && !hasBrk('+24V'))) {
    const p = page(needs24 ? $t("Besleme · 400/230 V, kumanda gerilimi ve 24 V DC") : $t("Besleme · 400/230 V ve kumanda gerilimi"));
    busbars(p, !hasBrk('L1') && !hasPot, specs.length > 0);
    S(p, 'text', 100, 19, { props: { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 } });
    // 230 V control voltage only for starters with a contactor circuit and start relays (drives under the PLC need none)
    if (!hasBrk('1L') && specs.some(rung)) {
      S(p, 'cb1', 72, 72, { tag: tag('F', 'CB'), desc: $t("Kumanda gerilimi 230 V"), part: has('OS-C6-1P') });
      W(p, [[72, 24], [72, 72]], '1.5', 'BK');
      if (safety) {
        // two safety outputs in series: the e-stop switches off the control voltage of every contactor
        W(p, [[72, 96], [72, 100]], '1.5', 'RD');
        S(p, 'no', 72, 100, { tag: safety });
        W(p, [[72, 116], [72, 124]], '1.5', 'RD');
        S(p, 'no', 72, 124, { tag: safety });
        W(p, [[72, 140], [72, 148]], '1.5', 'RD');
        S(p, 'brk', 72, 148, { props: { name: '1L', ptype: 'L' } });
        safetyWired = true;
      } else {
        W(p, [[72, 96], [72, 112]], '1.5', 'RD');
        S(p, 'brk', 72, 112, { props: { name: '1L', ptype: 'L' } });
      }
      W(p, [[112, 48], [112, 112]], '1.5', 'BU');
      S(p, 'brk', 112, 112, { props: { name: '1N', ptype: 'N' } });
    }
    if (needs24 && !hasBrk('+24V')) {
      S(p, 'cb1', 192, 72, { tag: tag('F', 'CB'), desc: '24 V DC besleme', part: has('OS-C6-1P') });
      W(p, [[192, 24], [192, 72]], '1.5', 'BK');
      W(p, [[192, 96], [192, 120]], '1.5', 'BK');
      W(p, [[216, 48], [216, 112], [200, 112], [200, 120]], '1.5', 'BU');
      S(p, 'psu', 192, 120, { tag: tag('G'), desc: '24 V DC besleme', part: has('GK-24-5') });
      W(p, [[192, 144], [192, 168]], '1', 'DBU');
      S(p, 'brk', 192, 168, { props: { name: '+24V', ptype: 'DC+' } });
      W(p, [[200, 144], [200, 152], [232, 152], [232, 168]], '1', 'WH');
      S(p, 'brk', 232, 168, { props: { name: '0V', ptype: 'DC-' } });
    }
  }

  /* ---- emergency stop: safety relay, dual-channel e-stop chain, monitored reset ---- */
  if (nStop) {
    const p = page($t('Acil stop devresi 24 V DC'));
    S(p, 'brk', 32, 24, { rot: 1, props: { name: '+24V', ptype: 'DC+' } });
    W(p, [[32, 24], [396, 24]], '1', 'DBU');
    S(p, 'brk', 32, 224, { rot: 1, props: { name: '0V', ptype: 'DC-' } });
    W(p, [[32, 224], [396, 224]], '1', 'WH');
    const x0 = X(3), c1 = X(1), c2 = X(6), yR = 120;
    S(p, 'safrel', x0, yR, { tag: safety, desc: $t('Acil stop güvenlik rölesi'), part: has('GR-24DC-3NO') });
    W(p, [[x0 + 8, 24], [x0 + 8, yR]]);
    W(p, [[x0 + 8, yR + 32], [x0 + 8, 224]]);
    // channel 1 through the e-stops on the left, channel 2 through their second contacts on the right: no crossings
    W(p, [[x0, yR], [x0, 32], [c1, 32], [c1, 40]]);
    W(p, [[x0 + 16, yR], [x0 + 16, 32], [c2, 32], [c2, 40]]);
    let y = 40;
    for (let i = 0; i < nStop; i++) {
      const t = tag('S');
      S(p, 'estop', c1, y, { tag: t, desc: $t('Acil stop {0}', i + 1) });
      S(p, 'nc', c2, y, { tag: t });
      y += 16;
      if (i < nStop - 1) {
        W(p, [[c1, y], [c1, y + 8]]);
        W(p, [[c2, y], [c2, y + 8]]);
        y += 8;
      }
    }
    W(p, [[c1, y], [c1, 208], [x0, 208], [x0, yR + 32]]);
    W(p, [[c2, y], [c2, 208], [x0 + 16, 208], [x0 + 16, yR + 32]]);
    S(p, 'pb_no', x0 + 24, 80, { tag: tag('S'), desc: $t('Acil stop reset'), part: has('BT-MV-1NO') });
    W(p, [[x0 + 24, 96], [x0 + 24, yR]]);
    W(p, [[x0 + 24, 80], [x0 + 24, 72], [x0 + 32, 72], [x0 + 32, yR]]);
    warnings.push($t('Acil stop devresi bir şablondur: güvenlik fonksiyonunun kategorisini ve performans seviyesini (ISO 13849-1), güvenlik rölesinin seçimini ve gerekiyorsa kontaktör geri besleme (EDM) devresini doğrulayın.'));
    if (!safetyWired && specs.some(rung))
      warnings.push($t('Güvenlik rölesi kontakları kumanda gerilimine (1L) otomatik bağlanamadı; kesilecek devreye elle ekleyin.'));
  }

  /* ---- device tags per motor ---- */
  interface Dev {
    spec: MotorSpec;
    M: string;
    In: number;
    cs: string;
    Q: string;
    F?: string;
    K1?: string;
    K2?: string;
    K3?: string;
    KT?: string;
    T?: string;
    /** start relay of a soft starter or drive (230 V coil, its contact on the start input) */
    KR?: string;
    /** hand-off-auto selector */
    SA?: string;
    /** thermistor relay: its output contact 11-14 above the overload's in the control rung */
    PTC?: string;
    /** hand-off-auto without PLC: the field contacts that start the motor in auto (forward, reverse) */
    AB: string[];
    KA: string[];
    signals: Signal[];
  }
  const devs: Dev[] = specs.map((spec) => {
    let M = spec.tag || '';
    if (!M || taken.has(M)) {
      if (M) warnings.push($t("{0} etiketi projede zaten var; yeni etiket verildi", M));
      M = tag('M');
    } else claim(M);
    taken.add(M);
    const In = ratedCurrent(spec.kw);
    const d: Dev = { spec, M, In, cs: cableSection(spec.kind === 'yd' ? In / Math.sqrt(3) : In), Q: tag('Q'), KA: [], AB: [], signals: [] };
    const viaPlc = usesPlc(spec), hoa = handAutoOf(spec);
    if (hoa) d.SA = tag('S', 'SS');
    if (hoa && !viaPlc) d.AB = spec.kind === 'rev' ? [tag('B'), tag('B')] : [tag('B')];
    // the PLC sees the selector in auto
    const inAuto = (): Signal => ({ dir: 'in', desc: $t("{0} otomatikte", M), contact: { tag: d.SA!, type: 'rot_no' } });
    if (spec.kind === 'vfd' || spec.kind === 'ss') {
      d.T = spec.kind === 'ss' ? tag('T', 'SST') : tag('T');
      if (rung(spec)) d.KR = tag('K');
      if (spec.ptc) warnings.push($t('{0}: PTC termistörleri {1} cihazının termistör girişine bağlanır; ayrı termistör rölesi çizilmedi.', M, d.T));
      if (viaPlc) {
        d.KA.push(tag('K'));
        d.signals.push({ dir: 'out', desc: $t("{0} çalıştır", M), relay: d.KA[0] });
        d.signals.push({ dir: 'in', desc: spec.kind === 'ss' ? $t("{0} soft starter arızası", M) : $t("{0} sürücü arızası", M), contact: { tag: d.T, type: 'no' } });
        if (hoa) d.signals.push(inAuto());
        if (spec.speedRef && spec.kind === 'vfd') d.signals.push({ dir: 'ao', desc: $t("{0} hız referansı", M), load: { tag: d.T, type: 'vfd_ai', desc: '' } });
      }
      return d;
    }
    d.K1 = tag('K', starter);
    if (spec.kind === 'rev') d.K2 = tag('K', starter);
    if (spec.kind === 'yd') {
      d.K2 = tag('K', starter); // delta
      d.K3 = tag('K', starter); // star
      d.KT = tag('K', 'TR'); // timer
    }
    d.F = tag('F');
    if (spec.ptc) d.PTC = tag('F');
    if (viaPlc) {
      d.KA.push(tag('K'));
      if (spec.kind === 'rev') d.KA.push(tag('K'));
      d.signals.push({ dir: 'out', desc: spec.kind === 'rev' ? $t("{0} ileri", M) : $t("{0} çalıştır", M), relay: d.KA[0] });
      if (spec.kind === 'rev') d.signals.push({ dir: 'out', desc: $t("{0} geri", M), relay: d.KA[1] });
      d.signals.push({ dir: 'in', desc: spec.kind === 'rev' ? $t("{0} ileri çalışıyor", M) : $t("{0} çalışıyor", M), contact: { tag: d.K1, type: 'no' } });
      if (spec.kind === 'rev') d.signals.push({ dir: 'in', desc: $t("{0} geri çalışıyor", M), contact: { tag: d.K2!, type: 'no' } });
      d.signals.push({ dir: 'in', desc: $t("{0} termik arıza", M), contact: { tag: d.F, type: 'no' } });
      if (hoa) d.signals.push(inAuto());
    }
    return d;
  });

  /* ---- power pages ---- */
  const width: Record<StarterKind, number> = { dol: 2, rev: 2, vfd: 2, ss: 2, yd: 4 };
  let pp: Page | null = null, col = COLS + 1, onPage: string[] = [];
  const flush = () => {
    if (pp) pp.desc = $t("Güç devresi · {0}", onPage.join(', '));
  };
  for (const d of devs) {
    const w = width[d.spec.kind];
    if (!pp || col + w - 1 > COLS) {
      flush();
      pp = page('');
      busbars(pp, false);
      col = 1;
      onPage = [];
    }
    power(pp, d, X(col));
    onPage.push(d.M);
    col += w;
  }
  flush();

  function terminals(p: Page, xs: number[], y: number, peX: number) {
    for (const x of [...xs, peX]) {
      term++;
      S(p, 'term', x, y, { tag: 'X1', part: has(x === peX ? 'KL-4-PE' : 'KL-2.5'), props: { pinNo: String(term) } });
    }
  }
  function motorAndCable(p: Page, d: Dev, x: number, yTerm: number, pins: number[], peX: number) {
    const yM = yTerm + 24, spec = d.spec;
    pins.forEach((px) => W(p, [[px, yTerm + 8], [px, yM]], d.cs, 'BK'));
    W(p, [[peX, yTerm + 8], [peX, yM]], d.cs, 'GNYE');
    const six = spec.kind === 'yd';
    S(p, six ? 'motor6' : 'motor3', x, yM, {
      tag: d.M,
      desc: `${spec.desc ? spec.desc + ' · ' : ''}${f1(spec.kw)} kW`,
      part: choose(d.M, 'motor', six ? 'motor6' : 'motor3', { kw: spec.kw }),
    });
    const len = peX - x + 16;
    S(p, 'cable', x - 8, yTerm + 16, {
      tag: tag('W'),
      props: {
        len,
        cableType: spec.kind === 'vfd' ? 'NYCY' : 'NYY-J',
        cores: six ? '1,2,3,4,5,6,PE' : '1,2,3,PE',
        cs: d.cs,
        ...(spec.cableLen ? { length: String(spec.cableLen) } : {}),
      },
    });
  }
  function feed(p: Page, x: number, yTo: number, cs: string) {
    ([[x, 24], [x + 8, 32], [x + 16, 40]] as const).forEach(([px, y]) => W(p, [[px, y], [px, yTo]], cs, 'BK'));
  }
  function down(p: Page, xs: number[], y0: number, y1: number, cs: string) {
    xs.forEach((px) => W(p, [[px, y0], [px, y1]], cs, 'BK'));
  }

  function power(p: Page, d: Dev, x: number) {
    const { spec, M, In, cs } = d;
    const plc = spec.control === 'plc', xs = [x, x + 8, x + 16];
    if (spec.kind === 'dol') {
      feed(p, x, 64, cs);
      S(p, 'cb3', x, 64, { tag: d.Q, desc: $t("{0} motor koruma", M), part: choose(M, $t("motor koruma şalteri"), 'cb3', { amps: In }) });
      S(p, 'k3', x, 100, { tag: d.K1 });
      down(p, xs, 88, 100, cs);
      S(p, 'ol3', x, 132, { tag: d.F, desc: $t("{0} aşırı yük", M), part: choose(M, $t("termik röle"), 'ol3', { amps: In, no: 1, nc: 1 }) });
      down(p, xs, 120, 132, cs);
      down(p, xs, 152, 164, cs);
      terminals(p, xs, 164, x + 24);
      W(p, [[x + 48, 56], [x + 48, 158], [x + 24, 158], [x + 24, 164]], cs, 'GNYE');
      motorAndCable(p, d, x, 164, xs, x + 24);
    } else if (spec.kind === 'rev') {
      feed(p, x, 60, cs);
      S(p, 'cb3', x, 60, { tag: d.Q, desc: $t("{0} motor koruma", M), part: choose(M, $t("motor koruma şalteri"), 'cb3', { amps: In }) });
      down(p, xs, 84, 108, cs);
      // reversing contactor swaps L1 and L3
      W(p, [[x, 92], [x + 56, 92], [x + 56, 108]], cs, 'BK');
      W(p, [[x + 8, 96], [x + 48, 96], [x + 48, 108]], cs, 'BK');
      W(p, [[x + 16, 100], [x + 40, 100], [x + 40, 108]], cs, 'BK');
      S(p, 'k3', x, 108, { tag: d.K1 });
      S(p, 'k3', x + 40, 108, { tag: d.K2 });
      // the two contactors are interlocked mechanically, from the link of one to the link of the other
      S(p, 'mint', x + 14, 117.5);
      down(p, xs, 128, 148, cs);
      W(p, [[x + 40, 128], [x + 40, 132], [x, 132]], cs, 'BK');
      W(p, [[x + 48, 128], [x + 48, 136], [x + 8, 136]], cs, 'BK');
      W(p, [[x + 56, 128], [x + 56, 140], [x + 16, 140]], cs, 'BK');
      S(p, 'ol3', x, 148, { tag: d.F, desc: $t("{0} aşırı yük", M), part: choose(M, $t("termik röle"), 'ol3', { amps: In, no: 1, nc: 1 }) });
      down(p, xs, 168, 180, cs);
      terminals(p, xs, 180, x + 24);
      W(p, [[x + 64, 56], [x + 64, 174], [x + 24, 174], [x + 24, 180]], cs, 'GNYE');
      motorAndCable(p, d, x, 180, xs, x + 24);
    } else if (spec.kind === 'yd') {
      // up to where the line splits to the line and delta contactors the conductors carry the full line current,
      // after it the phase current (In / √3)
      const Iph = In / Math.sqrt(3), d2 = [x + 48, x + 56, x + 64], csL = cableSection(In);
      feed(p, x, 60, csL);
      S(p, 'cb3', x, 60, { tag: d.Q, desc: $t("{0} motor koruma", M), part: choose(M, $t("motor koruma şalteri"), 'cb3', { amps: In }) });
      down(p, xs, 84, 100, csL);
      W(p, [[x, 88], [x + 48, 88], [x + 48, 100]], cs, 'BK');
      W(p, [[x + 8, 92], [x + 56, 92], [x + 56, 100]], cs, 'BK');
      W(p, [[x + 16, 96], [x + 64, 96], [x + 64, 100]], cs, 'BK');
      S(p, 'k3', x, 100, { tag: d.K1 });
      S(p, 'k3', x + 48, 100, { tag: d.K2 });
      down(p, xs, 120, 132, cs);
      S(p, 'ol3', x, 132, { tag: d.F, desc: $t("{0} aşırı yük (faz akımı)", M), part: choose(M, $t("termik röle"), 'ol3', { amps: Iph, no: 1, nc: 1 }) });
      down(p, xs, 152, 172, cs);
      down(p, d2, 120, 172, cs);
      W(p, [[x + 48, 124], [x + 96, 124], [x + 96, 136]], cs, 'BK');
      W(p, [[x + 56, 128], [x + 104, 128], [x + 104, 136]], cs, 'BK');
      W(p, [[x + 64, 132], [x + 112, 132], [x + 112, 136]], cs, 'BK');
      S(p, 'k3y', x + 96, 136, { tag: d.K3 });
      // star and delta contactors interlocked mechanically: from the delta contactor's link, round the right of the
      // star contactor to its link (clear of the wiring between them)
      S(p, 'mint', x + 62, 109.5, { props: { pts: '0,0 58,0 58,36 48,36' } });
      terminals(p, [...xs, ...d2], 172, x + 72);
      W(p, [[x + 80, 56], [x + 80, 166], [x + 72, 166], [x + 72, 172]], cs, 'GNYE');
      motorAndCable(p, d, x, 172, [...xs, ...d2], x + 72);
    } else if (spec.kind === 'ss') {
      feed(p, x, 60, cs);
      S(p, 'cb3', x, 60, { tag: d.Q, desc: $t("{0} motor koruma", M), part: choose(M, $t("motor koruma şalteri"), 'cb3', { amps: In }) });
      down(p, xs, 84, 100, cs);
      S(p, 'ss', x, 100, { tag: d.T, desc: $t("{0} soft starter", M), part: choose(M, $t("soft starter"), 'ss', { minKw: spec.kw, no: 1 }), props: { kw: String(spec.kw) } });
      runContact(p, d, x);
      down(p, xs, 132, 164, cs);
      terminals(p, xs, 164, x + 24);
      W(p, [[x + 48, 56], [x + 48, 158], [x + 24, 158], [x + 24, 164]], cs, 'GNYE');
      motorAndCable(p, d, x, 164, xs, x + 24);
    } else {
      feed(p, x, 60, cs);
      S(p, 'cb3', x, 60, { tag: d.Q, desc: $t("{0} hat koruma", M), part: choose(M, $t("motor koruma şalteri"), 'cb3', { amps: In }) });
      down(p, xs, 84, 100, cs);
      S(p, 'vfd', x, 100, {
        tag: d.T,
        desc: $t("{0} frekans konvertörü", M),
        part: choose(M, $t("frekans konvertörü"), 'vfd', { minKw: spec.kw, no: d.KA.length || d.KR ? 1 : 0 }),
        props: { kw: String(spec.kw) },
      });
      runContact(p, d, x);
      down(p, xs, 132, 144, cs);
      W(p, [[x + 24, 132], [x + 24, 144]], cs, 'GNYE');
      terminals(p, xs, 144, x + 24);
      W(p, [[x + 48, 56], [x + 48, 138], [x + 24, 138]], cs, 'GNYE');
      motorAndCable(p, d, x, 144, xs, x + 24);
    }
  }

  /** Start command of a drive or soft starter: a relay contact across its start input and its own common. */
  function runContact(p: Page, d: Dev, x: number) {
    const run = d.KR ?? d.KA[0];
    if (!run) return;
    S(p, 'no', x + 32, 76, { tag: run });
    W(p, [[x + 32, 92], [x + 32, 100]]);
    W(p, [[x + 32, 76], [x + 32, 72], [x + 40, 72], [x + 40, 100]]);
  }

  /* ---- control pages (230 V AC) ---- */
  const ctrlCols = (d: Dev) => {
    // hand-auto: one more column for the auto branch, one less where the PLC reports faults instead of a lamp
    const plc = d.spec.control === 'plc', auto = d.spec.control === 'auto', hoa = handAutoOf(d.spec);
    switch (d.spec.kind) {
      case 'dol': return plc ? 2 : auto ? 5 : 4;
      case 'rev': return plc ? 4 : hoa ? (auto ? 9 : 8) : 7;
      case 'yd': return plc ? 8 : 9;
      default: return rung(d.spec) ? (auto ? 5 : 4) : 0;
    }
  };
  let cp: Page | null = null;
  col = COLS + 1;
  onPage = [];
  const flushC = () => {
    if (cp) cp.desc = $t("Kumanda devresi 230 V · {0}", onPage.join(', '));
  };
  for (const d of devs) {
    const w = ctrlCols(d);
    if (!w) continue;
    if (!cp || col + w - 1 > COLS) {
      flushC();
      cp = page('');
      S(cp, 'brk', 32, 24, { rot: 1, props: { name: '1L', ptype: 'L' } });
      W(cp, [[32, 24], [396, 24]], '1.5', 'RD');
      S(cp, 'brk', 32, 224, { rot: 1, props: { name: '1N', ptype: 'N' } });
      W(cp, [[32, 224], [396, 224]], '1.5', 'BU');
      col = 1;
      onPage = [];
    }
    control(cp, d, X(col));
    onPage.push(d.M);
    col += w;
  }
  flushC();

  /* ---- thermistor relays: supply A1-A2 from the control voltage, the motor's PTC leads on T1-T2 over terminals ---- */
  const ptcs = devs.filter((d) => d.PTC);
  if (ptcs.length) {
    let tp: Page | null = null, k = COLS + 1, names: string[] = [];
    const flushT = () => {
      if (tp) tp.desc = $t('Motor termistör koruması 230 V · {0}', names.join(', '));
    };
    for (const d of ptcs) {
      if (!tp || k > COLS) {
        flushT();
        tp = page('');
        S(tp, 'brk', 32, 24, { rot: 1, props: { name: '1L', ptype: 'L' } });
        W(tp, [[32, 24], [396, 24]], '1.5', 'RD');
        S(tp, 'brk', 32, 224, { rot: 1, props: { name: '1N', ptype: 'N' } });
        W(tp, [[32, 224], [396, 224]], '1.5', 'BU');
        k = 1;
        names = [];
      }
      const x = X(k);
      W(tp, [[x, 24], [x, 40]]);
      S(tp, 'ptcr', x, 40, { tag: d.PTC, desc: $t('{0} termistör koruma', d.M), part: has('TR-PTC-230') });
      W(tp, [[x, 60], [x, 224]]);
      for (const dx of [12, 20]) {
        W(tp, [[x + dx, 60], [x + dx, 140]]);
        term++;
        S(tp, 'term', x + dx, 140, { tag: 'X1', part: has('KL-2.5'), props: { pinNo: String(term) } });
        W(tp, [[x + dx, 148], [x + dx, 164]]);
      }
      S(tp, 'text', x + 8, 172, { props: { text: `→ ${d.M} PTC`, size: 2.5 } });
      names.push(d.M);
      k++;
    }
    flushT();
    warnings.push($t('Termistör rölesi bir şablondur: klemens numaralarını (A1-A2, T1-T2, 11-12/14) seçilen rölenin bağlantı şemasıyla kontrol edin; motorun PTC uçlarını güç kablosundan ayrı bir kabloyla getirin.'));
  }

  /* ---- safe torque off: each drive's STO input through a safety output of the e-stop relay ---- */
  const drives = safety ? devs.filter((d) => d.spec.kind === 'vfd') : [];
  if (drives.length) {
    let sp: Page | null = null, k = COLS + 1;
    for (const d of drives) {
      if (!sp || k > COLS) {
        sp = page($t('Güvenli tork kesme (STO) 24 V DC'));
        S(sp, 'brk', 32, 24, { rot: 1, props: { name: '+24V', ptype: 'DC+' } });
        W(sp, [[32, 24], [396, 24]], '1', 'DBU');
        S(sp, 'brk', 32, 224, { rot: 1, props: { name: '0V', ptype: 'DC-' } });
        W(sp, [[32, 224], [396, 224]], '1', 'WH');
        k = 1;
      }
      const x = X(k);
      W(sp, [[x, 24], [x, 40]]);
      S(sp, 'no', x, 40, { tag: safety });
      W(sp, [[x, 56], [x, 72]]);
      S(sp, 'vfd_sto', x, 72, { tag: d.T, desc: $t('{0} güvenli tork kesme', d.M) });
      W(sp, [[x, 88], [x, 224]]);
      k++;
    }
    warnings.push($t('STO bağlantısı tek kanallı bir şablondur: sürücülerin STO1/STO2 girişlerini ve istenen güvenlik seviyesini üretici kılavuzuna göre doğrulayın.'));
    const outs = (safetyWired ? 2 : 0) + (needsPlc ? 1 : 0) + drives.length;
    if (outs > 3) warnings.push($t('Güvenlik rölesinin {0} çıkışı kullanıldı; seçilen rölenin çıkış sayısını kontrol edin, gerekirse genişletme modülü ekleyin.', outs));
  }

  function coil(p: Page, x: number, y: number, t: string, desc: string, part: string) {
    S(p, 'coil', x, y, { tag: t, desc, part });
  }
  function contactor(d: Dev, t: string, w: PartWant) {
    return choose(d.M, $t("{0} kontaktörü", t), 'coil', { text: '230 V AC', ...w });
  }
  /** The overload's NC contact at the top of the rung (straight wire where there is no overload relay). */
  function guard(p: Page, d: Dev, x: number) {
    if (!d.F) return void W(p, [[x, 24], [x, 56]]);
    // the thermistor relay's contact (closed while the windings are cool and the relay is supplied) straight from the rail
    if (d.PTC) S(p, 'no', x, 24, { tag: d.PTC, props: { pins: '11-14' } });
    else W(p, [[x, 24], [x, 40]]);
    S(p, 'nc', x, 40, { tag: d.F });
  }
  /** F2 NC → [stop → start ‖ seal] or [interposing relay]; returns the y where the rung continues. */
  function head(p: Page, d: Dev, x: number, sealTag: string, relay: string | undefined, startDesc = 'Start'): number {
    if (d.SA) return handAuto(p, d, x, sealTag, startDesc, x + (d.spec.kind === 'yd' ? 120 : 80));
    guard(p, d, x);
    if (relay) {
      W(p, [[x, 56], [x, 100]]);
      S(p, 'no', x, 100, { tag: relay });
      return 116;
    }
    W(p, [[x, 56], [x, 68]]);
    S(p, 'pb_nc', x, 68, { tag: tag('S'), desc: $t("{0} stop", d.M), part: has('BT-KR-1NC') });
    W(p, [[x, 84], [x, 100]]);
    return startBranch(p, d, x, sealTag, startDesc);
  }
  /**
   * Hand-off-auto: the selector's hand contact → stop → start ‖ seal at x; its auto contact → the PLC's relay (or the
   * field contact) in column xo, joining after the seal. Returns the y where the rung continues (the join is at 128).
   */
  function handAuto(p: Page, d: Dev, x: number, sealTag: string, desc: string, xo: number): number {
    guard(p, d, x);
    W(p, [[x, 56], [x, 64]]);
    W(p, [[x, 60], [xo, 60], [xo, 64]]);
    S(p, 'rot01', x, 64, { tag: d.SA, desc: $t("{0} el-0-otomatik seçici", d.M), part: has('SEC-ELOTO-3NO') });
    W(p, [[x, 80], [x, 84]]);
    S(p, 'pb_nc', x, 84, { tag: tag('S'), desc: $t("{0} stop", d.M), part: has('BT-KR-1NC') });
    W(p, [[x, 100], [x, 108]]);
    W(p, [[x, 104], [x + 40, 104], [x + 40, 108]]);
    S(p, 'pb_no', x, 108, { tag: tag('S'), desc: `${d.M} ${desc.toLocaleLowerCase('tr')}`, part: has('BT-YS-1NO') });
    S(p, 'no', x + 40, 108, { tag: sealTag });
    W(p, [[x + 40, 124], [x + 40, 128], [x, 128]]);
    S(p, 'rot_no', xo, 64, { tag: d.SA });
    W(p, [[xo, 80], [xo, 84]]);
    autoContact(p, d, xo, 84, 0);
    W(p, [[xo, 100], [xo, 128], [x + 40, 128]]);
    return 124;
  }
  /** What starts the motor in auto: the PLC's relay, or a field contact (float, pressure switch…) without a PLC. */
  function autoContact(p: Page, d: Dev, x: number, y: number, i: number) {
    if (d.KA[i]) return void S(p, 'no', x, y, { tag: d.KA[i] });
    const desc = d.AB.length > 1 ? (i ? $t("{0} geri otomatik kontağı", d.M) : $t("{0} ileri otomatik kontağı", d.M)) : $t("{0} otomatik çalıştırma kontağı", d.M);
    S(p, 'ls_no', x, y, { tag: d.AB[i], desc });
  }
  function startBranch(p: Page, d: Dev, x: number, sealTag: string, desc: string): number {
    S(p, 'pb_no', x, 100, { tag: tag('S'), desc: `${d.M} ${desc.toLocaleLowerCase('tr')}`, part: has('BT-YS-1NO') });
    S(p, 'no', x + 40, 100, { tag: sealTag });
    W(p, [[x, 92], [x + 40, 92], [x + 40, 100]]);
    W(p, [[x + 40, 116], [x + 40, 124], [x, 124]]);
    return 116;
  }
  function lamp(p: Page, x: number, contact: string, t: string, desc: string, pn: string) {
    W(p, [[x, 24], [x, 56]]);
    S(p, 'no', x, 56, { tag: contact });
    W(p, [[x, 72], [x, 140]]);
    S(p, 'lamp', x, 140, { tag: t, desc, part: has(pn) });
    W(p, [[x, 156], [x, 224]]);
  }

  function control(p: Page, d: Dev, x: number) {
    const { spec, M } = d, plc = spec.control === 'plc', hoa = handAutoOf(spec), viaPlc = usesPlc(spec);
    // contacts of the contactor: seal and lamp, the PLC's feedback, or all three
    const noAux = plc ? 1 : viaPlc ? 3 : 2;
    // lamps beside the rung (one column further right with the auto branch); where the PLC watches the motor it
    // reports the fault, the fault contact (one NO) goes to its input instead of a lamp
    const lx = x + (hoa ? 120 : 80);
    if (spec.kind === 'dol') {
      const y = head(p, d, x, d.K1!, d.KA[0]);
      W(p, [[x, y], [x, 140]]);
      coil(p, x, 140, d.K1!, $t("{0} kontaktörü", M), contactor(d, d.K1!, { minKw: spec.kw, no: noAux }));
      W(p, [[x, 160], [x, 224]]);
      if (!plc) {
        lamp(p, lx, d.K1!, tag('H'), $t("{0} çalışıyor", M), 'SL-230-G');
        if (!viaPlc) lamp(p, lx + 40, d.F!, tag('H'), $t("{0} termik arıza", M), 'SL-230-R');
      }
    } else if (d.KR) {
      // soft starter or hand-auto drive: a start relay whose contact closes the start input
      const y = head(p, d, x, d.KR, d.KA[0]);
      W(p, [[x, y], [x, 140]]);
      coil(p, x, 140, d.KR, $t("{0} çalıştırma rölesi", M), has('YR-4NO4NC-230AC'));
      W(p, [[x, 160], [x, 224]]);
      lamp(p, lx, d.KR, tag('H'), $t("{0} çalışıyor", M), 'SL-230-G');
      if (!viaPlc) lamp(p, lx + 40, d.T!, tag('H'), $t("{0} arıza", M), 'SL-230-R');
    } else if (spec.kind === 'rev' && hoa) {
      revHandAuto(p, d, x, noAux);
    } else if (spec.kind === 'rev') {
      // forward path at x, reverse path at x + 80 (local) / x + 80 (PLC); each with the other's NC interlock
      const xr = x + 80;
      guard(p, d, x);
      if (plc) {
        W(p, [[x, 56], [x, 100]]);
        W(p, [[x, 72], [xr, 72], [xr, 100]]);
        S(p, 'no', x, 100, { tag: d.KA[0] });
        S(p, 'no', xr, 100, { tag: d.KA[1] });
      } else {
        W(p, [[x, 56], [x, 68]]);
        S(p, 'pb_nc', x, 68, { tag: tag('S'), desc: $t("{0} stop", M), part: has('BT-KR-1NC') });
        W(p, [[x, 84], [x, 100]]);
        W(p, [[x, 88], [xr, 88], [xr, 100]]);
        startBranch(p, d, x, d.K1!, $t("İleri"));
        startBranch(p, d, xr, d.K2!, 'Geri');
      }
      for (const [cx, own, other] of [[x, d.K1!, d.K2!], [xr, d.K2!, d.K1!]] as const) {
        W(p, [[cx, 116], [cx, 132]]);
        S(p, 'nc', cx, 132, { tag: other });
        W(p, [[cx, 148], [cx, 160]]);
        coil(p, cx, 160, own, $t("{0} {1} kontaktörü", M, own === d.K1 ? 'ileri' : 'geri'), contactor(d, own, { minKw: spec.kw, no: noAux, nc: 1 }));
        W(p, [[cx, 180], [cx, 224]]);
      }
      if (!plc) {
        lamp(p, x + 160, d.K1!, tag('H'), $t("{0} ileri", M), 'SL-230-G');
        lamp(p, x + 200, d.K2!, tag('H'), $t("{0} geri", M), 'SL-230-G');
        lamp(p, x + 240, d.F!, tag('H'), $t("{0} termik arıza", M), 'SL-230-R');
      }
    } else if (spec.kind === 'yd') {
      const y = head(p, d, x, d.K1!, d.KA[0]);
      const kw = spec.kw / Math.sqrt(3);
      W(p, [[x, y], [x, 148]]);
      W(p, [[x, 132], [x + 240, 132]]);
      coil(p, x, 148, d.K1!, $t("{0} şebeke kontaktörü", M), contactor(d, d.K1!, { minKw: kw, no: noAux }));
      W(p, [[x, 168], [x, 224]]);
      // timer
      W(p, [[x + 80, 132], [x + 80, 148]]);
      // on-delay time relay: its coil marked slow-operating (IEC 60617 S00312)
      S(p, 'coil_on', x + 80, 148, { tag: d.KT, desc: $t("{0} Y/Δ zaman rölesi", M), part: choose(M, $t("zaman rölesi"), 'coil_on', { text: 'Zaman rölesi' }) });
      W(p, [[x + 80, 168], [x + 80, 224]]);
      // star (KT NC, delta NC) and delta (KT NO, star NC)
      for (const [cx, t, first, other, desc, star] of [
        [x + 160, d.K3!, 'nc_t', d.K2!, $t("yıldız"), true],
        [x + 240, d.K2!, 'no_t', d.K3!, $t("üçgen"), false],
      ] as const) {
        W(p, [[cx, 132], [cx, 140]]);
        S(p, first, cx, 140, { tag: d.KT });
        W(p, [[cx, 156], [cx, 160]]);
        S(p, 'nc', cx, 160, { tag: other });
        W(p, [[cx, 176], [cx, 184]]);
        coil(p, cx, 184, t, $t("{0} {1} kontaktörü", M, desc), contactor(d, t, { minKw: star ? spec.kw / 3 : kw, nc: 1 }));
        W(p, [[cx, 204], [cx, 224]]);
      }
      if (!plc) {
        lamp(p, x + 280, d.K1!, tag('H'), $t("{0} çalışıyor", M), 'SL-230-G');
        if (!viaPlc) lamp(p, x + 320, d.F!, tag('H'), $t("{0} termik arıza", M), 'SL-230-R');
      }
    }
  }

  /**
   * Reversing starter, hand-off-auto: hand contact → stop → forward start ‖ seal (x) and reverse start ‖ seal
   * (x + 80); auto contact (x + 160) → the PLC's forward and reverse relays or the field contacts (x + 160, x + 200),
   * joining each direction above its interlock. Lamps from x + 240 (the fault lamp only without a PLC).
   */
  function revHandAuto(p: Page, d: Dev, x: number, noAux: number) {
    const M = d.M, xr = x + 80, xa = x + 160, xb = x + 200;
    guard(p, d, x);
    W(p, [[x, 56], [x, 64]]);
    W(p, [[x, 60], [xa, 60], [xa, 64]]);
    S(p, 'rot01', x, 64, { tag: d.SA, desc: $t("{0} el-0-otomatik seçici", M), part: has('SEC-ELOTO-3NO') });
    W(p, [[x, 80], [x, 84]]);
    S(p, 'pb_nc', x, 84, { tag: tag('S'), desc: $t("{0} stop", M), part: has('BT-KR-1NC') });
    W(p, [[x, 100], [x, 112]]);
    W(p, [[x, 104], [xr, 104], [xr, 112]]);
    for (const [cx, seal, desc] of [[x, d.K1!, $t("İleri")], [xr, d.K2!, 'Geri']] as const) {
      W(p, [[cx, 108], [cx + 40, 108], [cx + 40, 112]]);
      S(p, 'pb_no', cx, 112, { tag: tag('S'), desc: `${M} ${desc.toLocaleLowerCase('tr')}`, part: has('BT-YS-1NO') });
      S(p, 'no', cx + 40, 112, { tag: seal });
      W(p, [[cx + 40, 128], [cx + 40, 132], [cx, 132]]);
    }
    // auto: the selector's auto contact, then the PLC's forward relay (joins the forward rung at 136, crossing the
    // reverse rung) and reverse relay (joins at 140)
    S(p, 'rot_no', xa, 64, { tag: d.SA });
    W(p, [[xa, 80], [xa, 88]]);
    W(p, [[xa, 84], [xb, 84], [xb, 88]]);
    autoContact(p, d, xa, 88, 0);
    autoContact(p, d, xb, 88, 1);
    W(p, [[xa, 104], [xa, 136], [x, 136]]);
    W(p, [[xb, 104], [xb, 140], [xr, 140]]);
    for (const [cx, own, other] of [[x, d.K1!, d.K2!], [xr, d.K2!, d.K1!]] as const) {
      W(p, [[cx, 128], [cx, 144]]);
      S(p, 'nc', cx, 144, { tag: other });
      W(p, [[cx, 160], [cx, 164]]);
      coil(p, cx, 164, own, $t("{0} {1} kontaktörü", M, own === d.K1 ? 'ileri' : 'geri'), contactor(d, own, { minKw: d.spec.kw, no: noAux, nc: 1 }));
      W(p, [[cx, 184], [cx, 224]]);
    }
    lamp(p, x + 240, d.K1!, tag('H'), $t("{0} ileri", M), 'SL-230-G');
    lamp(p, x + 280, d.K2!, tag('H'), $t("{0} geri", M), 'SL-230-G');
    if (!usesPlc(d.spec)) lamp(p, x + 320, d.F!, tag('H'), $t("{0} termik arıza", M), 'SL-230-R');
  }

  /* ---- field signals: switches on inputs, lamps and valves on outputs ---- */
  const ioSignals = ioSpecs.map((io, i): { label: string; s: Signal } => {
    const prefix = io.kind === 'lamp' ? 'H' : io.kind === 'valve' || io.kind === 'valve_prop' ? 'MB' : 'B';
    const t = ioTags[i] || tag(prefix);
    taken.add(t);
    const desc = io.desc || t;
    if (io.kind === 'lamp') return { label: t, s: { dir: 'out', desc, load: { tag: t, type: 'lamp_led', desc } } };
    if (io.kind === 'valve') return { label: t, s: { dir: 'out', desc, load: { tag: t, type: 'sol', desc } } };
    if (io.kind === 'valve_prop') return { label: t, s: { dir: 'ao', desc, load: { tag: t, type: 'vprop', desc } } };
    if (io.kind === 'analog') return { label: t, s: { dir: 'ai', desc, contact: { tag: t, type: 'xmtr', desc } } };
    // the switch drawn with its actuator: named in the type column, else in the description ("Hat basıncı" → pressure)
    const type = sensorSymbol(io.sensor ?? parseSensor(io.desc), io.kind === 'switch_nc');
    return { label: t, s: { dir: 'in', desc, contact: { tag: t, type, desc } } };
  });

  // the PLC sees whether the e-stop circuit is released
  if (safety && needsPlc) ioSignals.unshift({ label: safety, s: { dir: 'in', desc: $t('Acil stop devresi hazır'), contact: { tag: safety, type: 'no' } } });

  /* ---- PLC pages (24 V DC) ---- */
  const signals = [...devs.flatMap((d) => d.signals.map((s) => ({ label: d.M, s }))), ...ioSignals];
  if (signals.length) {
    const nIn = signals.filter((x) => x.s.dir === 'in').length, nAi = signals.filter((x) => x.s.dir === 'ai').length, nAo = signals.filter((x) => x.s.dir === 'ao').length, nOut = signals.length - nIn - nAi - nAo;
    const count = (type: string) => pr.pages.reduce((n, p) => n + p.syms.filter((s) => s.type === type).length, 0);
    const inBase = count('plcin'), outBase = count('plcout'), aiBase = count('plcai'), aoBase = count('plcao');
    const inCards = Array.from({ length: Math.ceil(nIn / 16) }, () => tag('A'));
    const outCards = Array.from({ length: Math.ceil(nOut / 16) }, () => tag('A'));
    const aiCards = Array.from({ length: Math.ceil(nAi / 4) }, () => tag('A'));
    const aoCards = Array.from({ length: Math.ceil(nAo / 2) }, () => tag('A'));
    const addr = (pre: string, n: number) => `${pre}${Math.floor(n / 8)}.${n % 8}`;
    let iIn = 0, iOut = 0, iAi = 0, iAo = 0, lp: Page | null = null, from = '', to = '';
    col = COLS + 1;
    const flushL = () => {
      if (lp) lp.desc = $t("PLC sinyalleri 24 V DC · {0}", from === to ? from : `${from}–${to}`);
    };
    for (const { label, s } of signals) {
      const w = s.dir === 'out' ? 2 : 1;
      if (!lp || col + w - 1 > COLS) {
        flushL();
        lp = page('');
        S(lp, 'brk', 32, 24, { rot: 1, props: { name: '+24V', ptype: 'DC+' } });
        W(lp, [[32, 24], [396, 24]], '1', 'DBU');
        S(lp, 'brk', 32, 224, { rot: 1, props: { name: '0V', ptype: 'DC-' } });
        W(lp, [[32, 224], [396, 224]], '1', 'WH');
        col = 1;
        from = label;
      }
      to = label;
      const x = X(col);
      if (s.dir === 'ao') {
        // analog output (QW64, QW66 …) → load → 0 V: the drive's set point input or a proportional valve
        S(lp, 'plcao', x, 40, { tag: aoCards[Math.floor(iAo / 2)], desc: s.desc, part: has('PLC-AQ2'), props: { addr: 'QW' + (64 + (aoBase + iAo) * 2) } });
        W(lp, [[x, 52], [x, 72]]);
        S(lp, s.load!.type, x, 72, { tag: s.load!.tag, desc: s.load!.desc });
        W(lp, [[x, 88], [x, 224]]);
        iAo++;
      } else if (s.dir === 'ai') {
        // 2-wire loop: +24 V → transmitter → analog input (word addresses IW64, IW66 …)
        W(lp, [[x, 24], [x, 40]]);
        S(lp, 'xmtr', x, 40, { tag: s.contact!.tag, desc: s.contact!.desc ?? '' });
        W(lp, [[x, 56], [x, 68]]);
        S(lp, 'plcai', x, 68, { tag: aiCards[Math.floor(iAi / 4)], desc: s.desc, part: has('PLC-AI4'), props: { addr: 'IW' + (64 + (aiBase + iAi) * 2) } });
        iAi++;
      } else if (s.dir === 'in') {
        W(lp, [[x, 24], [x, 40]]);
        // a switch of its own on the input is out in the field (a motor's contacts are those of its starter)
        S(lp, s.contact!.type, x, 40, { tag: s.contact!.tag, desc: s.contact!.desc ?? '', ...(['no', 'nc', 'rot_no'].includes(s.contact!.type) ? {} : { props: { field: '1' } }) });
        W(lp, [[x, 56], [x, 68]]);
        S(lp, 'plcin', x, 68, { tag: inCards[Math.floor(iIn / 16)], desc: s.desc, part: has('PLC-DI16'), props: { addr: addr('I', inBase + iIn) } });
        iIn++;
      } else {
        S(lp, 'plcout', x, 40, { tag: outCards[Math.floor(iOut / 16)], desc: s.desc, part: has('PLC-DQ16'), props: { addr: addr('Q', outBase + iOut) } });
        if (s.load) {
          // a load on the output itself: 24 V lamp or solenoid valve, back to 0 V
          W(lp, [[x, 52], [x, 72]]);
          S(lp, s.load.type, x, 72, { tag: s.load.tag, desc: s.load.desc });
          W(lp, [[x, s.load.type === 'sol' ? 92 : 88], [x, 224]]);
        } else {
          W(lp, [[x, 52], [x, 72]]);
          S(lp, 'coil', x, 72, { tag: s.relay, desc: $t("{0} ara rölesi", s.desc), part: has('RL-2CO-24DC') });
          W(lp, [[x, 92], [x, 224]]);
        }
        iOut++;
      }
      col += w;
    }
    flushL();
  }

  // the safety relay with as many outputs as are drawn: an expansion module beyond three
  if (safety) {
    const outs = pages.flatMap((p) => p.syms).filter((s) => s.tag === safety && s.type === 'no').length;
    const sr = pages.flatMap((p) => p.syms).find((s) => s.type === 'safrel' && s.tag === safety);
    if (sr && outs > 3) sr.part = has('GR-24DC-7NO') || sr.part;
  }
  return { pages, warnings };
}
